import * as babylon from "@babylonjs/core";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { useVizDispatch } from "@/shared/state/visualization";
import { setThreeDModel } from "@/shared/state/visualization/actions";
import type { File } from "@/shared/state/visualization/schema/v1/file";
import type {
  EntityID,
  EntityRegistry,
  JointValue,
} from "@/shared/state/visualization/schema/v1/three-d";

import { ModelLoader } from "../../../Panel/ThreeDPanel/ModelLoader";
import { makeFirstMessageReader } from "../../../Panel/ThreeDPanel/ModelLoader/firstMessageReader";
import { readJointsFromArticulation } from "../../../Panel/ThreeDPanel/ModelLoader/readJoints";
import {
  resolveDefaultBindings,
  withDefaultBindings,
  type BindingFailure,
} from "../../../Panel/ThreeDPanel/ModelLoader/resolveDefaultBindings";
import {
  selectAllWorkspaceTopicTrees,
  useEphemeralWorkspaceState,
} from "../../../WorkspaceCtx/hooks";

interface ImportState {
  /** True while a file is being fetched and parsed, so the row can show progress. */
  importing?: boolean;
  error?: string;
  /**
   * Joints whose declared default binding matched nothing, by joint name.
   *
   * Never an error: a model whose topics are not open still imports and still
   * renders, at rest. This is what lets a row say *which* of the several
   * different "not bound" states it is in.
   */
  unresolvedBindings?: Record<string, BindingFailure>;
}

/**
 * Points one entity at a model file.
 *
 * This is the only way a model gets into the panel: an entity's Mesh names a
 * file, and that one component draws the file's whole node hierarchy. There is
 * no scene-level model list — geometry belongs to entities, so there is one
 * place it can come from and one thing to delete to remove it.
 *
 * ## Why this parses the file here
 *
 * Only for the joints. The panel's own scene fetches and instantiates the model
 * itself; what the settings need in addition is the articulation the file
 * declares — so the joint list renders without re-fetching the model each time
 * the sidebar opens, and so a saved workspace still lists its joints when the
 * file has moved or access to it has gone.
 *
 * Reading a glTF's extensions needs the document parsed, and that goes through
 * Babylon, which needs *a* scene. Rather than plumb the live scene out to the
 * sidebar — or push a "please import" flag through panel state for the ECS to
 * notice — this parses into a throwaway `NullEngine` scene and keeps only the
 * plain data. The panel's own scene then instantiates from the same loader's
 * template cache, so the duplicated parse costs one extra fetch at import time
 * and nothing thereafter.
 *
 * ## Why this stays a hook, and why binding happens here
 *
 * A model file may declare, per joint, the topic and message path that drive it.
 * Resolving those declarations needs the topics the **workspace** has open,
 * which is context — so this cannot become a plain function, and pushing the
 * import into the reducer would cut it off from the topics entirely.
 *
 * It happens on *import* rather than on some later "apply" step so that any
 * future path into a model (convert-then-add, a template, a drop) gets the same
 * treatment for free.
 */
export function useImportModelIntoEntity(panelId: string) {
  const dispatch = useVizDispatch();
  const services = useDomainServices();
  const { files } = services;
  const [state, setState] = React.useState<ImportState>({});

  // One loader per file service, matching how the panel builds its own.
  const loader = React.useMemo(() => new ModelLoader(files), [files]);

  // Every topic open in the workspace, across all its files — the candidate set
  // a declared binding is matched against, exactly.
  const workspaceState = useEphemeralWorkspaceState();
  const topicTrees = selectAllWorkspaceTopicTrees(workspaceState);

  /**
   * @param file the glTF to draw — already resolved, never a robot description
   * @param entityId the entity that will draw it
   * @param registry the panel's entities, so an entity deleted while the picker
   *   was open is caught here rather than leaving a mesh nothing renders
   * @param source the file the *user* chose, when that was not `file` itself.
   *   Recorded alongside it so the settings can name the model by something the
   *   user recognises, and so a re-ingest is detectable later. Nothing about
   *   loading uses it.
   * @param jointValues what the entity is currently driven by, when it already
   *   draws a model. A joint named here keeps its value: it is a decision the
   *   user made, and a declared default never overrides one.
   * @returns whether the model was set, so the caller can dismiss itself.
   */
  const importModel = React.useCallback(
    async (
      file: File,
      entityId: EntityID,
      registry: EntityRegistry,
      source?: File,
      jointValues?: Readonly<Record<string, JointValue>>,
    ): Promise<boolean> => {
      if (!registry[entityId]) {
        setState({ error: "That entity is no longer in this panel" });
        return false;
      }
      setState({ importing: true });
      const engine = new babylon.NullEngine();
      const scene = new babylon.Scene(engine);
      // Aborted only by a failure below; the read is bounded by one message per
      // topic, so there is no long-running fetch for a user to cancel — but the
      // reads still need a signal to stop on when the import gives up.
      const aborter = new AbortController();
      try {
        const { container, articulation } = await loader.load(
          file.fileId,
          scene,
          aborter.signal,
        );
        // Nothing here draws it: the document was parsed for its extensions
        // alone, and the panel's scene makes its own copy from the template this
        // load has now cached.
        //
        // Nothing here measures it either, and that is deliberate. An import
        // used to carry a camera measured off this container, which the reducer
        // adopted by *switching the panel to third person* — and a third-person
        // camera does not track, so a robot bound to an odometry topic drove out
        // of frame. An import leaves the camera's type alone now; pointing the
        // orbit camera at a particular link of the model is a control in the
        // camera settings (`camera.target.node`) rather than something an import
        // decides.
        container.dispose();

        const joints = readJointsFromArticulation(articulation);
        const { bound, unresolved } = await resolveDefaultBindings(
          joints,
          topicTrees,
          makeFirstMessageReader(services),
          aborter.signal,
        );

        dispatch(
          setThreeDModel(panelId, {
            entityId,
            file,
            ...(source ? { source } : {}),
            joints,
            jointValues: withDefaultBindings(joints, jointValues, bound),
          }),
        );
        setState(
          Object.keys(unresolved).length > 0
            ? { unresolvedBindings: unresolved }
            : {},
        );
        return true;
      } catch (e: unknown) {
        aborter.abort();
        if (isAbortError(e)) {
          setState({});
          return false;
        }
        const error = e instanceof Error ? e : new Error(String(e));
        setState({ error: error.message });
        ErrorMonitoringService.captureError(error);
        return false;
      } finally {
        scene.dispose();
        engine.dispose();
      }
    },
    [dispatch, loader, panelId, services, topicTrees],
  );

  const clearError = React.useCallback(() => {
    setState((current) => ({ ...current, error: undefined }));
  }, []);

  return { ...state, importModel, clearError };
}
